import type { JSONContent } from '@tiptap/react';
import { diffChars, diffWords, type Change } from 'diff';
import {
  ECodeBlockType,
  EDiffStatus,
  EDiffMark,
  type IBlockDiff,
  type IRowDiff,
} from '../text-diffs/utils/diff-types';
import {
  computeStructuredDiff,
  isChangedBlock,
} from '../text-diffs/utils/diff-utils';

// =============================================================================
// Constants
// =============================================================================

/** Maps non-list block types to TipTap node types */
const BLOCK_TYPE_TO_NODE: Record<
  ECodeBlockType.HEADING | ECodeBlockType.PARAGRAPH,
  string
> = {
  [ECodeBlockType.HEADING]: 'heading',
  [ECodeBlockType.PARAGRAPH]: 'paragraph',
};

/** Regex pattern for parsing bold markdown */
const BOLD_PATTERN = /\*\*([^*]+?)\*\*/g;

// =============================================================================
// Text Node Helpers
// =============================================================================

/**
 * TipTap represents text as nodes with an array of marks for formatting.
 * This helper constructs the proper node structure and marks.
 *
 * @param text - The text content for the node
 * @param marks - Array of mark objects (e.g., { type: 'bold' }, { type: 'diffAdded' })
 * @returns A TipTap JSONContent text node
 *
 * @example
 * createTextNode('hello', [{ type: 'bold' }])
 * // Returns: { type: 'text', text: 'hello', marks: [{ type: 'bold' }] }
 */
function createTextNode(
  text: string,
  marks: Array<{ type: string }> = [],
): JSONContent {
  const node: JSONContent = { type: 'text', text };
  if (marks.length > 0) node.marks = marks;
  return node;
}

/**
 * Parses inline markdown formatting and applies diff marks to the resulting nodes.
 *
 * Currently supports bold markdown (**text**). The diff mark is applied to all
 * generated nodes, allowing text to be both bold AND marked as added/deleted.
 *
 * TODO: Add support for italic, code, and strikethrough if AOP will have them.
 *
 * @param text - The text content that may contain markdown formatting
 * @param diffMark - Optional diff mark to apply (EDiffMark.ADDED or EDiffMark.DELETED)
 * @returns Array of TipTap text nodes with appropriate marks
 *
 * @example
 * parseInlineMarkdown('Hello **world**', EDiffMark.ADDED)
 * // Returns: [
 * //   { type: 'text', text: 'Hello ', marks: [{ type: 'diffAdded' }] },
 * //   { type: 'text', text: 'world', marks: [{ type: 'bold' }, { type: 'diffAdded' }] }
 * // ]
 */
function parseInlineMarkdown(
  text: string,
  diffMark?: EDiffMark,
): JSONContent[] {
  const nodes: JSONContent[] = [];
  const diffMarks = diffMark ? [{ type: diffMark }] : [];
  let lastIndex = 0;
  let match;

  while ((match = BOLD_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(createTextNode(text.slice(lastIndex, match.index), diffMarks));
    }
    nodes.push(createTextNode(match[1], [{ type: 'bold' }, ...diffMarks]));
    lastIndex = match.index + match[0].length;
  }

  BOLD_PATTERN.lastIndex = 0;

  if (lastIndex < text.length) {
    nodes.push(createTextNode(text.slice(lastIndex), diffMarks));
  }

  return nodes;
}

// =============================================================================
// Diff to TipTap Conversion Helpers
// =============================================================================

/**
 * Char-level diff should only be used for pure additions or pure removals.
 *
 * @example
 * - "apple" vs "apples": only additions → char-level OK
 * - "walking" vs "walk": only removals → char-level OK
 * - "two" vs "four": both additions and removals → word-level
 * - "hello" vs "Hello": both additions and removals (remove h, add H) → word-level
 */
function isReplacement(changes: Change[]): boolean {
  let hasAdditions = false;
  let hasRemovals = false;

  for (const change of changes) {
    if (change.added) hasAdditions = true;
    else if (change.removed) hasRemovals = true;

    if (hasAdditions && hasRemovals) return true;
  }

  return false;
}

/**
 * Checks if two strings share a common prefix or suffix.
 *
 * If strings share no common characters at the start or end, they are
 * likely completely different words and char-level diff would be noisy.
 *
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns True if strings share at least one common character at start or end
 *
 * @example
 * hasCommonPrefixOrSuffix('apple', 'apples') // true - common prefix 'apple'
 * hasCommonPrefixOrSuffix('walking', 'talking') // true - common suffix 'alking'
 * hasCommonPrefixOrSuffix('cat', 'dog') // false - no common prefix or suffix
 */
function hasCommonPrefixOrSuffix(a: string, b: string): boolean {
  const aTrimmed = a.trim();
  const bTrimmed = b.trim();
  if (!aTrimmed || !bTrimmed) return false;

  return (
    aTrimmed[0] === bTrimmed[0] || aTrimmed.slice(-1) === bTrimmed.slice(-1)
  );
}

/**
 * Converts an array of diff changes to TipTap text nodes with appropriate marks.
 *
 * Each change from the diff library is converted to one or more TipTap text nodes.
 * Unchanged text gets no diff marks, added text gets ADDED marks, removed text gets DELETED marks.
 * Inline markdown (bold) is preserved within each change.
 *
 * @param changes - Array of Change objects from the diff library
 * @returns Array of TipTap text nodes ready for rendering
 *
 */
function changesToNodes(changes: Change[]): JSONContent[] {
  return changes.flatMap(change => {
    if (!change.value) return [];
    const mark = change.removed
      ? EDiffMark.DELETED
      : change.added
        ? EDiffMark.ADDED
        : undefined;
    return parseInlineMarkdown(change.value, mark);
  });
}

/**
 * Computes diff and converts to TipTap text nodes.
 * Applies word-level diff first, then for each word replacement,
 * decides whether to use char-level or word-level.
 *
 * Optimization: skips diffChars if words share no common prefix/suffix.
 *
 * @example
 * - "Add" → "Adds": common prefix "Add" → char-level: Add[+s]
 * - "two" → "four": no common prefix/suffix → word-level: [-two][+four]
 */
function computeCharDiffNodes(oldStr: string, newStr: string): JSONContent[] {
  const wordChanges = diffWords(oldStr, newStr);
  const result: JSONContent[] = [];

  for (let i = 0; i < wordChanges.length; i++) {
    const change = wordChanges[i];
    const nextChange = wordChanges[i + 1];

    // Unchanged text
    if (!change.added && !change.removed) {
      result.push(...parseInlineMarkdown(change.value));
      continue;
    }

    // Word replacement: removed followed by added
    if (change.removed && nextChange?.added) {
      // Quick check: only use char-level diff if there is a common prefix or suffix
      if (hasCommonPrefixOrSuffix(change.value, nextChange.value)) {
        const charChanges = diffChars(change.value, nextChange.value);

        // If the char changes are a replacement, use word-level diff i.e. "test" -> "toast" would not be t[-est][+oast] but [-test][+toast]
        if (isReplacement(charChanges)) {
          result.push(...parseInlineMarkdown(change.value, EDiffMark.DELETED));
          result.push(
            ...parseInlineMarkdown(nextChange.value, EDiffMark.ADDED),
          );
        } else {
          result.push(...changesToNodes(charChanges));
        }
      } else {
        // No common prefix/suffix = definitely a replacement
        result.push(...parseInlineMarkdown(change.value, EDiffMark.DELETED));
        result.push(...parseInlineMarkdown(nextChange.value, EDiffMark.ADDED));
      }

      i++; // Skip next change since we processed it
      continue;
    }

    // Single addition or removal
    const mark = change.added ? EDiffMark.ADDED : EDiffMark.DELETED;
    result.push(...parseInlineMarkdown(change.value, mark));
  }

  return result;
}

/**
 * Gets the appropriate TipTap content nodes based on the diff status.
 *
 * Routes to the correct rendering strategy based on how the block changed:
 * - UNCHANGED: Render new content without diff marks
 * - ADDED: Render new content with addition marks
 * - REMOVED: Render old content with deletion marks
 * - MODIFIED: Compute character-level diff between old and new
 *
 * @param status - The diff status of the block
 * @param oldContent - The original content (used for REMOVED and MODIFIED)
 * @param newContent - The new content (used for UNCHANGED, ADDED, and MODIFIED)
 * @returns Array of TipTap text nodes with appropriate formatting and diff marks
 */
function getContentForStatus(
  status: EDiffStatus,
  oldContent: string,
  newContent: string,
): JSONContent[] {
  switch (status) {
    case EDiffStatus.UNCHANGED:
      return parseInlineMarkdown(newContent);
    case EDiffStatus.ADDED:
      return parseInlineMarkdown(newContent, EDiffMark.ADDED);
    case EDiffStatus.REMOVED:
      return parseInlineMarkdown(oldContent, EDiffMark.DELETED);
    case EDiffStatus.MODIFIED:
      return computeCharDiffNodes(oldContent, newContent);
    default:
      return [];
  }
}

/**
 * Type guard that checks if a block type is a list type.
 *
 * List blocks require special handling in TipTap because they must be
 * wrapped in a parent list node (orderedList or bulletList) with listItem children.
 *
 * @param blockType - The block type to check
 * @returns True if the block type is ORDERED_LIST or UNORDERED_LIST
 */
function isListBlockType(
  blockType: ECodeBlockType,
): blockType is ECodeBlockType.ORDERED_LIST | ECodeBlockType.UNORDERED_LIST {
  return (
    blockType === ECodeBlockType.ORDERED_LIST ||
    blockType === ECodeBlockType.UNORDERED_LIST
  );
}

// =============================================================================
// Table Diff Rendering
// =============================================================================

/**
 * Renders one table cell's content for a given row status.
 *
 * ADDED/REMOVED rows mark every cell wholesale. For UNCHANGED/MODIFIED rows,
 * each cell is compared independently — a row can be MODIFIED overall while
 * most of its cells are identical, so only the cells that actually changed
 * get a char-level diff instead of the whole row.
 */
function cellDiffNodes(
  oldCell: string | undefined,
  newCell: string | undefined,
  rowStatus: EDiffStatus,
): JSONContent[] {
  if (rowStatus === EDiffStatus.ADDED) {
    return parseInlineMarkdown(newCell ?? '', EDiffMark.ADDED);
  }
  if (rowStatus === EDiffStatus.REMOVED) {
    return parseInlineMarkdown(oldCell ?? '', EDiffMark.DELETED);
  }
  if (oldCell === newCell) {
    return parseInlineMarkdown(newCell ?? oldCell ?? '');
  }
  return computeCharDiffNodes(oldCell ?? '', newCell ?? '');
}

/**
 * Converts a table block's row diffs into a real TipTap `table` node —
 * `tableHeader` cells for the header row, `tableCell` for the rest — instead
 * of the raw `| pipe | separated |` markdown rendering as plain text.
 *
 * Column count is taken from the widest row across the whole table (not
 * per-row) so every `tableRow` has a consistent cell count, which the
 * table schema requires.
 *
 * The change-navigation anchor (`anchorIndex`) is stamped on the CHANGED rows
 * only — never the whole `<table>` — so hovering/scrolling a table change
 * highlights just the rows that changed instead of boxing the entire table.
 * If (defensively) no row is marked changed, the table itself gets the anchor
 * so the sidebar entry still resolves to something.
 */
function createTableDiffNode(
  rowDiffs: IRowDiff[],
  anchorIndex?: number,
): JSONContent | null {
  if (rowDiffs.length === 0) return null;

  const columnCount = rowDiffs.reduce(
    (max, row) =>
      Math.max(max, row.oldCells?.length ?? 0, row.newCells?.length ?? 0),
    0,
  );
  if (columnCount === 0) return null;

  let anchoredAnyRow = false;
  const tableRows: JSONContent[] = rowDiffs.map(row => {
    const cells: JSONContent[] = [];
    for (let c = 0; c < columnCount; c++) {
      const content = cellDiffNodes(
        row.oldCells?.[c],
        row.newCells?.[c],
        row.status,
      );
      cells.push({
        type: row.isHeader ? 'tableHeader' : 'tableCell',
        content: [{ type: 'paragraph', content }],
      });
    }
    const rowNode: JSONContent = { type: 'tableRow', content: cells };
    if (anchorIndex != null && row.status !== EDiffStatus.UNCHANGED) {
      rowNode.attrs = { dataDiffAnchor: String(anchorIndex) };
      anchoredAnyRow = true;
    }
    return rowNode;
  });

  const table: JSONContent = { type: 'table', content: tableRows };
  if (anchorIndex != null && !anchoredAnyRow) {
    table.attrs = { dataDiffAnchor: String(anchorIndex) };
  }
  return table;
}

/**
 * Creates a TipTap node from a block diff.
 *
 * Converts a single IBlockDiff into the appropriate TipTap JSONContent structure.
 * - Headings: Creates heading node with level attribute
 * - Paragraphs: Creates paragraph node
 * - Lists: Creates listItem node (to be wrapped by parent list later)
 * - Tables: Creates a real table/tableRow/tableHeader/tableCell structure
 *   with per-cell diff marks, instead of rendering raw markdown as text.
 *
 * The content with appropriate diff marks applied for additions, deletions, and modifications.
 *
 * @param diff - The block diff containing status, type, and content
 * @returns A TipTap JSONContent node, or null if the diff is invalid
 */
function createDiffNode(
  diff: IBlockDiff,
  anchorIndex?: number,
  isDiscarded = false,
): JSONContent | null {
  const { status, blockType, oldContent = '', newContent = '' } = diff;

  // Stamp the change's navigation anchor (see DiffAnchor extension) onto the
  // block's top-level node so the sidebar can scroll to it. Only changed
  // blocks get an index (anchorIndex is undefined for context blocks).
  const withAnchor = (node: JSONContent | null): JSONContent | null => {
    if (!node || anchorIndex == null) return node;
    node.attrs = { ...node.attrs, dataDiffAnchor: String(anchorIndex) };
    return node;
  };

  if (blockType === ECodeBlockType.TABLE) {
    // A discarded table change reverts to the committed table: drop rows the
    // edit would have ADDED and render every surviving row as its original
    // (old) cells, unmarked. Otherwise render the full row-level diff.
    const rowDiffs = isDiscarded
      ? (diff.rowDiffs ?? [])
          .filter(row => row.status !== EDiffStatus.ADDED)
          .map(row => ({
            ...row,
            status: EDiffStatus.UNCHANGED,
            newCells: row.oldCells,
          }))
      : (diff.rowDiffs ?? []);
    // Tables anchor their changed rows internally (not the whole table), so
    // pass the index straight through rather than stamping the <table> node.
    return createTableDiffNode(rowDiffs, anchorIndex);
  }

  // A discarded change reverts to the committed text: a block the edit ADDED
  // did not exist originally, so drop it; anything else renders its original
  // (old) content, unmarked, exactly as it stood before the edit.
  if (isDiscarded && status === EDiffStatus.ADDED) return null;

  const content = isDiscarded
    ? parseInlineMarkdown(oldContent)
    : getContentForStatus(status, oldContent, newContent);

  if (isListBlockType(blockType)) {
    return withAnchor({
      type: 'listItem',
      content: [{ type: 'paragraph', content }],
    });
  }

  const nodeType =
    BLOCK_TYPE_TO_NODE[
      blockType as ECodeBlockType.HEADING | ECodeBlockType.PARAGRAPH
    ];
  const attrs =
    blockType === ECodeBlockType.HEADING && diff.level
      ? { level: diff.level }
      : undefined;

  return withAnchor({ type: nodeType, attrs, content });
}

// =============================================================================
// Exported Functions
// =============================================================================

/**
 * Converts structured diff results to TipTap JSON content.
 * Nests unordered items under the preceding ordered item.
 *
 * @param diffs - Array of block diffs from computeStructuredDiff
 * @returns TipTap JSONContent document
 */
export function diffToTipTapContent(
  diffs: IBlockDiff[],
  discardedAnchors?: ReadonlySet<number>,
): JSONContent {
  const content: JSONContent[] = [];

  // Open-list stack for reconstructing nesting from each item's `depth`. Each
  // frame is a list node plus the last item pushed into it — a deeper item
  // nests under that item. Frames are strictly ascending by depth. Without
  // this, nested sub-steps (indented `1.`/`-` items) would be flattened into
  // one top-level list and renumbered 1..N.
  type ListFrame = {
    list: JSONContent;
    type: 'orderedList' | 'bulletList';
    depth: number;
    currentItem: JSONContent;
  };
  const stack: ListFrame[] = [];

  // Sequential anchor index over changed blocks only — must match the ordering
  // in getDiffChangeSummaries so a sidebar row's index maps to the right
  // `data-diff-anchor` in the DOM. Incremented via the shared isChangedBlock
  // predicate.
  let changeIndex = 0;

  for (const diff of diffs) {
    const anchorIndex = isChangedBlock(diff) ? changeIndex++ : undefined;
    const isDiscarded =
      anchorIndex != null && (discardedAnchors?.has(anchorIndex) ?? false);
    const node = createDiffNode(diff, anchorIndex, isDiscarded);
    if (!node) continue;

    const isList =
      diff.blockType === ECodeBlockType.ORDERED_LIST ||
      diff.blockType === ECodeBlockType.UNORDERED_LIST;

    if (!isList) {
      // A non-list block ends any open list.
      stack.length = 0;
      content.push(node);
      continue;
    }

    const listType =
      diff.blockType === ECodeBlockType.ORDERED_LIST
        ? 'orderedList'
        : 'bulletList';
    const depth = diff.depth ?? 0;

    // Returned to a shallower level: close every deeper frame.
    while (stack.length > 0 && stack[stack.length - 1].depth > depth) {
      stack.pop();
    }

    const top = stack[stack.length - 1];
    if (top && top.depth === depth && top.type === listType) {
      // Sibling item in the same list.
      top.list.content!.push(node);
      top.currentItem = node;
      continue;
    }

    // A new list is needed — a deeper level, the first list, or a list-type
    // switch at the same depth. Drop a same-depth frame of a different type so
    // the new list attaches beside it (under the enclosing item), not inside.
    if (top && top.depth === depth) stack.pop();
    const parent = stack[stack.length - 1];
    const list: JSONContent = { type: listType, content: [node] };
    if (parent) {
      // Nest under the parent (shallower) list's current item.
      parent.currentItem.content!.push(list);
    } else {
      content.push(list);
    }
    stack.push({ list, type: listType, depth, currentItem: node });
  }

  return { type: 'doc', content };
}

/**
 * Computes diff between two markdown documents and returns TipTap content.
 *
 * This is the main entry point that combines structured diff computation
 * with TipTap conversion.
 *
 * @param oldCode - The old version of the markdown code
 * @param newCode - The new version of the markdown code
 * @param discardedAnchors - Anchor indices whose change should render reverted
 *   to the original (committed) text instead of the proposed edit. Lets the
 *   preview show a live "kept vs discarded" mix without re-diffing.
 * @returns TipTap JSONContent document with diff marks
 */
export function computeDiffContent(
  oldCode: string,
  newCode: string,
  discardedAnchors?: ReadonlySet<number>,
): JSONContent {
  return diffToTipTapContent(
    computeStructuredDiff(oldCode, newCode),
    discardedAnchors,
  );
}
